import { useEffect, useMemo, useState } from "react";
import { BrandMark } from "@ciphernest/ui";
import {
  DEFAULT_API_BASE,
  VaultController,
  type VaultCipher,
} from "@ciphernest/vault-core";
import {
  biometricAvailable,
  clearDeviceUnlock,
  disableBiometricUnlock,
  disablePinUnlock,
  enableBiometricUnlock,
  enablePinUnlock,
  isBioEnabled,
  isNativeApp,
  isPinEnabled,
  unlockWithBiometric,
  unlockWithPin,
  persistUnlockMeta,
} from "./deviceUnlock";
import {
  clearAutofillCache,
  clearPendingAutofillSave,
  isAutofillServiceEnabled,
  loadSecuritySettings,
  openAutofillSettings,
  publishAutofillCache,
  readPendingAutofillSave,
  saveSecuritySettings,
  shouldPromptAutofillSetup,
  snoozeAutofillPrompt,
  startSecurityWatch,
  type PendingAutofillSave,
  type SecuritySettings,
} from "./appSecurity";
import { App as CapApp } from "@capacitor/app";
import { copyText } from "./clipboard";
import { PasswordField } from "./PasswordField";
import { analyzePasswordHealth } from "./passwordHealth";
import { buildRecoveryKit } from "./recoveryKit";
import { generateTotp } from "./totp";
import { downloadText, parseImportFile } from "./vaultImport";
import {
  assertPasskey,
  clearPasskey,
  hasPasskey,
  registerPasskey,
  webauthnAvailable,
} from "./devicePasskey";

const vault = new VaultController(DEFAULT_API_BASE);

type Nav = "home" | "vault" | "tools" | "more";
type VaultFilter = "all" | "login" | "note" | "card" | "identity" | "fav" | "trash";
type NewKind = "login" | "note" | "card" | "identity" | null;

function initials(name: string) {
  const parts = name.replace(/\[.*?\]/g, "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

function avatarClass(type: number) {
  if (type === 2) return "note";
  if (type === 3) return "card";
  if (type === 4) return "id";
  return "";
}

function subtitle(c: VaultCipher) {
  if (c.type === 1) return c.login?.username || c.login?.uris?.[0]?.uri || "Login";
  if (c.type === 2) return "Secure note";
  if (c.type === 3) return c.card?.brand || c.card?.number?.slice(-4) || "Card";
  if (c.type === 4)
    return [c.identity?.firstName, c.identity?.lastName].filter(Boolean).join(" ") ||
      c.identity?.email ||
      "Identity";
  return "Item";
}

function useToast() {
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 1600);
    return () => clearTimeout(t);
  }, [toast]);
  return { toast, show: (m: string) => setToast(m) };
}

export function App() {
  const [, bump] = useState(0);
  const refresh = () => bump((n) => n + 1);
  if (!vault.session) return <LockScreen onUnlocked={refresh} />;
  return <VaultApp onChange={refresh} />;
}

function PendingSavePrompt({
  pending,
  onDone,
  show,
  setError,
  onOpen,
}: {
  pending: PendingAutofillSave;
  onDone: () => void;
  show: (m: string) => void;
  setError: (s: string | null) => void;
  onOpen: (id: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="sp-drawer">
      <div className="sp-drawer-panel" role="dialog">
        <div className="sp-sheet-head">
          <div>
            <h2 className="brand">Save this login?</h2>
            <p className="sp-sub" style={{ margin: "0.35rem 0 0" }}>
              Android autofill captured credentials for{" "}
              <strong>{pending.name}</strong>
              {pending.username ? ` (${pending.username})` : ""}.
            </p>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
          <button
            type="button"
            className="sp-btn"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await vault.saveLogin({
                  name: pending.name,
                  username: pending.username,
                  password: pending.password,
                  uri: pending.uri || null,
                });
                await clearPendingAutofillSave();
                await publishAutofillCache(vault.ciphers);
                show("Login saved");
                const created = vault.ciphers.find(
                  (c) =>
                    c.type === 1 &&
                    c.name === pending.name &&
                    c.login?.username === pending.username
                );
                if (created) onOpen(created.id);
                onDone();
              } catch (err) {
                setError(err instanceof Error ? err.message : "Save failed");
              } finally {
                setBusy(false);
              }
            }}
          >
            Save to vault
          </button>
          <button
            type="button"
            className="sp-btn sp-btn-ghost"
            disabled={busy}
            onClick={async () => {
              await clearPendingAutofillSave();
              onDone();
            }}
          >
            Discard
          </button>
        </div>
      </div>
    </div>
  );
}

function AutofillSetupPrompt({
  onDone,
  show,
}: {
  onDone: () => void;
  show: (m: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const sub = CapApp.addListener("appStateChange", ({ isActive }) => {
      if (!isActive) return;
      void (async () => {
        const { enabled } = await isAutofillServiceEnabled();
        if (enabled) {
          show("Autofill enabled");
          onDone();
        }
      })();
    });
    return () => {
      void sub.then((h) => h.remove());
    };
  }, [onDone, show]);

  return (
    <div className="sp-drawer">
      <div className="sp-drawer-panel" role="dialog">
        <div className="sp-sheet-head">
          <div>
            <h2 className="brand">Turn on autofill</h2>
            <p className="sp-sub" style={{ margin: "0.35rem 0 0" }}>
              CipherNest is not set as your phone’s autofill service. Enable it
              to get login suggestions above the keyboard in Chrome and other
              apps — like Dashlane.
            </p>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
          <button
            type="button"
            className="sp-btn"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await openAutofillSettings();
                show("Choose CipherNest in Autofill service");
              } catch (err) {
                show(err instanceof Error ? err.message : "Failed");
              } finally {
                setBusy(false);
              }
            }}
          >
            Open autofill settings
          </button>
          <button
            type="button"
            className="sp-btn sp-btn-ghost"
            disabled={busy}
            onClick={async () => {
              await snoozeAutofillPrompt();
              onDone();
            }}
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

function QuickUnlockSetup({
  onDone,
  show,
}: {
  onDone: () => void;
  show: (m: string) => void;
}) {
  const [pin1, setPin1] = useState("");
  const [pin2, setPin2] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [bioOk, setBioOk] = useState(false);

  useEffect(() => {
    void biometricAvailable().then(setBioOk);
  }, []);

  async function savePin() {
    setBusy(true);
    setError(null);
    try {
      if (pin1 !== pin2) throw new Error("PINs do not match");
      const snap = vault.getUnlockSnapshot();
      if (!snap) throw new Error("Vault is locked");
      await enablePinUnlock(pin1, snap);
      show("PIN unlock on");
      if (bioOk) {
        try {
          await enableBiometricUnlock(snap);
          show("PIN + fingerprint on");
        } catch {
          /* PIN alone is fine */
        }
      }
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function saveBioOnly() {
    setBusy(true);
    setError(null);
    try {
      const snap = vault.getUnlockSnapshot();
      if (!snap) throw new Error("Vault is locked");
      await enableBiometricUnlock(snap);
      show("Fingerprint unlock on");
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sp-drawer">
      <div className="sp-drawer-panel" role="dialog">
        <div className="sp-sheet-head">
          <div>
            <h2 className="brand">Unlock faster next time</h2>
            <p className="sp-sub" style={{ margin: "0.35rem 0 0" }}>
              Set a PIN and fingerprint like other password apps. Your master
              password stays only in your head.
            </p>
          </div>
        </div>
        {error ? <p className="sp-error">{error}</p> : null}
        <div className="sp-field">
          <label>PIN (4–8 digits)</label>
          <input
            inputMode="numeric"
            pattern="[0-9]*"
            value={pin1}
            onChange={(e) =>
              setPin1(e.target.value.replace(/\D/g, "").slice(0, 8))
            }
            placeholder="••••"
            autoFocus
          />
        </div>
        <div className="sp-field">
          <label>Confirm PIN</label>
          <input
            inputMode="numeric"
            pattern="[0-9]*"
            value={pin2}
            onChange={(e) =>
              setPin2(e.target.value.replace(/\D/g, "").slice(0, 8))
            }
            placeholder="••••"
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
          <button
            type="button"
            className="sp-btn"
            disabled={busy || pin1.length < 4}
            onClick={() => void savePin()}
          >
            {bioOk ? "Turn on PIN + fingerprint" : "Turn on PIN unlock"}
          </button>
          {bioOk ? (
            <button
              type="button"
              className="sp-btn sp-btn-ghost"
              disabled={busy}
              onClick={() => void saveBioOnly()}
            >
              Fingerprint only
            </button>
          ) : null}
          <button
            type="button"
            className="sp-btn sp-btn-ghost"
            disabled={busy}
            onClick={onDone}
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}

function LockScreen({ onUnlocked }: { onUnlocked: () => void }) {
  const [mode, setMode] = useState<"quick" | "password" | "register">("password");
  const [email, setEmail] = useState(
    () => localStorage.getItem("ciphernest.email") || ""
  );
  const [password, setPassword] = useState("");
  const [rememberEmail, setRememberEmail] = useState(
    () => localStorage.getItem("ciphernest.rememberEmail") !== "0"
  );
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [pinOn, setPinOn] = useState(false);
  const [bioOn, setBioOn] = useState(false);
  const [bioOk, setBioOk] = useState(false);
  const [need2fa, setNeed2fa] = useState(false);
  const [totpCode, setTotpCode] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [p, b, avail] = await Promise.all([
        isPinEnabled(),
        isBioEnabled(),
        biometricAvailable(),
      ]);
      if (cancelled) return;
      setPinOn(p);
      setBioOn(b);
      setBioOk(avail);
      if (p || b) setMode("quick");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (mode !== "quick" || !bioOn || !bioOk || busy) return;
    const t = setTimeout(() => {
      void tryBio();
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, bioOn, bioOk]);

  async function finishDeviceUnlock(
    snap: Awaited<ReturnType<typeof unlockWithPin>>
  ) {
    await vault.unlockFromSnapshot(snap);
    const fresh = vault.getUnlockSnapshot();
    if (fresh) await persistUnlockMeta(fresh);
    onUnlocked();
  }

  async function tryBio() {
    setBusy(true);
    setError(null);
    try {
      const snap = await unlockWithBiometric();
      await finishDeviceUnlock(snap);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Biometric unlock failed";
      if (!/cancel|user canceled|10|13/i.test(msg)) setError(msg);
    } finally {
      setBusy(false);
    }
  }

  async function tryPin(nextPin: string) {
    if (nextPin.length < 4) return;
    setBusy(true);
    setError(null);
    try {
      const snap = await unlockWithPin(nextPin);
      setPin("");
      await finishDeviceUnlock(snap);
    } catch (err) {
      setPin("");
      setError(err instanceof Error ? err.message : "Wrong PIN");
    } finally {
      setBusy(false);
    }
  }

  function tapDigit(d: string) {
    if (busy) return;
    setPin((p) => (p + d).slice(0, 8));
  }

  async function submitPin() {
    if (pin.length < 4) {
      setError("Enter at least 4 digits");
      return;
    }
    await tryPin(pin);
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      vault.setBaseUrl(DEFAULT_API_BASE);
      localStorage.setItem(
        "ciphernest.rememberEmail",
        rememberEmail ? "1" : "0"
      );
      if (rememberEmail) {
        localStorage.setItem("ciphernest.email", email.trim().toLowerCase());
      } else {
        localStorage.removeItem("ciphernest.email");
      }
      if (mode === "register") await vault.register(email, password);
      await vault.unlock(email, password, need2fa ? totpCode : undefined);
      setPassword("");
      setTotpCode("");
      setNeed2fa(false);
      const snap = vault.getUnlockSnapshot();
      if (snap) await persistUnlockMeta(snap);
      const [p, b] = await Promise.all([isPinEnabled(), isBioEnabled()]);
      if (isNativeApp() && !p && !b) {
        sessionStorage.setItem("ciphernest.offerQuickUnlock", "1");
      }
      onUnlocked();
    } catch (err) {
      const twoFactor =
        err &&
        typeof err === "object" &&
        "twoFactor" in err &&
        Boolean((err as { twoFactor?: boolean }).twoFactor);
      if (twoFactor) {
        setNeed2fa(true);
        setError("Enter your authenticator code");
      } else {
        setError(err instanceof Error ? err.message : "Unlock failed");
      }
    } finally {
      setBusy(false);
    }
  }

  if (mode === "quick") {
    return (
      <div className="sp-shell">
        <div className="sp-hero-lock">
          <div className="sp-lock-panel">
            <BrandMark />
            <p className="sp-sub">
              {email ? `Unlock ${email}` : "Unlock your vault"}
            </p>
            {error ? <p className="sp-error">{error}</p> : null}
            {bioOn && bioOk ? (
              <button
                type="button"
                className="sp-btn"
                style={{ width: "100%", marginBottom: "1rem" }}
                disabled={busy}
                onClick={() => void tryBio()}
              >
                {busy ? "Checking…" : "Unlock with fingerprint"}
              </button>
            ) : null}
            {pinOn ? (
              <>
                <p className="sp-section-title">Or enter PIN</p>
                <div className="sp-pin-dots">
                  {Array.from({ length: Math.max(4, pin.length || 4) }).map(
                    (_, i) => (
                      <span
                        key={i}
                        className={`sp-pin-dot ${i < pin.length ? "on" : ""}`}
                      />
                    )
                  )}
                </div>
                <div className="sp-pin-pad">
                  {"123456789".split("").map((d) => (
                    <button
                      key={d}
                      type="button"
                      className="sp-pin-key"
                      onClick={() => tapDigit(d)}
                    >
                      {d}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="sp-pin-key ghost"
                    onClick={() => setPin("")}
                  >
                    C
                  </button>
                  <button
                    type="button"
                    className="sp-pin-key"
                    onClick={() => tapDigit("0")}
                  >
                    0
                  </button>
                  <button
                    type="button"
                    className="sp-pin-key ghost"
                    onClick={() => setPin((p) => p.slice(0, -1))}
                  >
                    ⌫
                  </button>
                  <button
                    type="button"
                    className="sp-pin-key"
                    style={{ gridColumn: "1 / -1" }}
                    disabled={busy || pin.length < 4}
                    onClick={() => void submitPin()}
                  >
                    Unlock
                  </button>
                </div>
              </>
            ) : null}
            <button
              type="button"
              className="sp-btn sp-btn-ghost"
              style={{ width: "100%", marginTop: "1rem" }}
              onClick={() => setMode("password")}
            >
              Use master password
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sp-shell">
      <div className="sp-hero-lock">
        <form className="sp-lock-panel" onSubmit={submitPassword}>
          <BrandMark />
          <p className="sp-sub">
            {mode === "register"
              ? "Create your CipherNest account."
              : "Welcome back. Unlock your vault."}
          </p>
          <div className="sp-field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              required
            />
          </div>
          <PasswordField
            id="password"
            label="Master password"
            value={password}
            onChange={setPassword}
            autoComplete={
              mode === "register" ? "new-password" : "current-password"
            }
            required
          />
          <label className="sp-check">
            <input
              type="checkbox"
              checked={rememberEmail}
              onChange={(e) => setRememberEmail(e.target.checked)}
            />
            Remember email on this device
          </label>
          {mode !== "register" ? (
            <p className="sp-sub" style={{ marginTop: "-0.35rem" }}>
              Master password is never saved. After unlock, set PIN /
              fingerprint so you don’t type it every time.
            </p>
          ) : null}
          {need2fa ? (
            <div className="sp-field">
              <label htmlFor="totp2fa">Authenticator code</label>
              <input
                id="totp2fa"
                inputMode="numeric"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                required
                autoFocus
              />
            </div>
          ) : null}
          {error ? <p className="sp-error">{error}</p> : null}
          <button className="sp-btn" type="submit" disabled={busy} style={{ width: "100%" }}>
            {busy
              ? mode === "register"
                ? "Creating…"
                : "Unlocking…"
              : mode === "register"
                ? "Create account"
                : "Unlock"}
          </button>
          <button
            type="button"
            className="sp-btn sp-btn-ghost"
            style={{ marginTop: "0.75rem", width: "100%" }}
            onClick={() =>
              setMode((m) => (m === "register" ? "password" : "register"))
            }
          >
            {mode === "register"
              ? "I already have an account"
              : "Create an account"}
          </button>
          {pinOn || bioOn ? (
            <button
              type="button"
              className="sp-btn sp-btn-ghost"
              style={{ marginTop: "0.5rem", width: "100%" }}
              onClick={() => setMode("quick")}
            >
              Use PIN / fingerprint
            </button>
          ) : null}
        </form>
      </div>
    </div>
  );
}

function VaultApp({ onChange }: { onChange: () => void }) {
  const [nav, setNav] = useState<Nav>("home");
  const [filter, setFilter] = useState<VaultFilter>("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<VaultCipher | null>(null);
  const [newKind, setNewKind] = useState<NewKind>(null);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [offerQuick, setOfferQuick] = useState(false);
  const [offerAutofill, setOfferAutofill] = useState(false);
  const [pendingSave, setPendingSave] = useState<PendingAutofillSave | null>(
    null
  );
  const { toast, show } = useToast();

  useEffect(() => {
    if (sessionStorage.getItem("ciphernest.offerQuickUnlock") !== "1") return;
    void (async () => {
      if (!isNativeApp()) return;
      const [p, b] = await Promise.all([isPinEnabled(), isBioEnabled()]);
      if (!p && !b) setOfferQuick(true);
      else sessionStorage.removeItem("ciphernest.offerQuickUnlock");
    })();
  }, []);

  useEffect(() => {
    if (!isNativeApp()) return;
    let cancelled = false;
    async function checkAutofill() {
      if (await shouldPromptAutofillSetup()) {
        if (!cancelled) setOfferAutofill(true);
      } else if (!cancelled) {
        setOfferAutofill(false);
      }
    }
    async function checkPendingSave() {
      const pending = await readPendingAutofillSave();
      if (!cancelled) setPendingSave(pending);
    }
    void checkAutofill();
    void checkPendingSave();
    const sub = CapApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) {
        void checkAutofill();
        void checkPendingSave();
      }
    });
    return () => {
      cancelled = true;
      void sub.then((h) => h.remove());
    };
  }, []);

  useEffect(() => {
    if (!vault.session) return;
    void publishAutofillCache(vault.ciphers);
  }, [vault.session, vault.ciphers.length]);

  useEffect(() => {
    return startSecurityWatch({
      vault,
      isUnlocked: () => Boolean(vault.session),
      onLocked: () => onChange(),
    });
  }, [onChange]);

  const counts = useMemo(() => {
    const c = vault.ciphers;
    return {
      all: c.length,
      login: c.filter((x) => x.type === 1).length,
      note: c.filter((x) => x.type === 2).length,
      card: c.filter((x) => x.type === 3).length,
      identity: c.filter((x) => x.type === 4).length,
      fav: c.filter((x) => x.favorite).length,
      trash: vault.trash.length,
    };
  }, [vault.ciphers.length, vault.trash.length]);

  const items = useMemo(() => {
    if (filter === "trash") {
      const needle = q.trim().toLowerCase();
      return vault.trash.filter(
        (c) => !needle || c.name.toLowerCase().includes(needle)
      );
    }
    const needle = q.trim().toLowerCase();
    return vault.ciphers
      .filter((c) => {
        if (filter === "login" && c.type !== 1) return false;
        if (filter === "note" && c.type !== 2) return false;
        if (filter === "card" && c.type !== 3) return false;
        if (filter === "identity" && c.type !== 4) return false;
        if (filter === "fav" && !c.favorite) return false;
        if (!needle) return true;
        const hay = `${c.name} ${subtitle(c)}`.toLowerCase();
        return hay.includes(needle);
      })
      .sort((a, b) => Number(b.favorite) - Number(a.favorite) || a.name.localeCompare(b.name));
  }, [q, filter, vault.ciphers.length, vault.trash.length]);

  const favorites = useMemo(
    () => vault.ciphers.filter((c) => c.favorite).slice(0, 8),
    [vault.ciphers.length]
  );

  async function syncNow() {
    setBusy(true);
    setError(null);
    try {
      await vault.sync();
      onChange();
      show("Vault synced");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sync failed");
    } finally {
      setBusy(false);
    }
  }

  async function onCopy(
    label: string,
    value: string,
    e?: React.MouseEvent,
    item?: VaultCipher
  ) {
    e?.stopPropagation();
    if (item?.reprompt === 1 && /password|code|totp|cvv/i.test(label)) {
      if (await assertPasskey()) {
        /* ok */
      } else {
        const mp = window.prompt("Enter master password to copy");
        if (!mp || !(await vault.verifyMasterPassword(mp))) {
          setError("Master password required");
          return;
        }
      }
    }
    await copyText(value);
    show(`${label} copied`);
  }

  function openNew(kind: NewKind) {
    setShowAddMenu(false);
    setNewKind(kind);
    const base: VaultCipher = {
      id: "",
      type: kind === "note" ? 2 : kind === "card" ? 3 : kind === "identity" ? 4 : 1,
      name: "",
      notes: null,
      folderId: null,
      organizationId: null,
      favorite: false,
      reprompt: 0,
      login:
        kind === "login"
          ? { username: "", password: "", totp: null, uris: [] }
          : undefined,
      card:
        kind === "card"
          ? {
              cardholderName: "",
              brand: "",
              number: "",
              expMonth: "",
              expYear: "",
              code: "",
            }
          : undefined,
      identity:
        kind === "identity"
          ? {
              title: null,
              firstName: "",
              lastName: "",
              email: "",
              phone: "",
              username: null,
              address1: "",
              city: "",
              postalCode: "",
              country: "",
            }
          : undefined,
    };
    setSelected(base);
  }

  const title =
    nav === "home"
      ? "CipherNest"
      : nav === "vault"
        ? "Vault"
        : nav === "tools"
          ? "Tools"
          : "More";

  return (
    <div className="sp-shell">
      <div className="sp-app">
        <header className="sp-app-header">
          <h1 className="brand">{title}</h1>
          <p>
            {vault.session?.email} · {counts.all} items
          </p>
        </header>

        <main className="sp-main">
          {error ? <p className="sp-error">{error}</p> : null}

          {nav === "home" ? (
            <>
              <div className="sp-home-grid">
                <button type="button" className="sp-quick" onClick={() => { setNav("vault"); setFilter("all"); }}>
                  <strong>{counts.all}</strong>
                  <span>All items</span>
                </button>
                <button type="button" className="sp-quick" onClick={() => { setNav("vault"); setFilter("fav"); }}>
                  <strong>{counts.fav}</strong>
                  <span>Favorites</span>
                </button>
                <button type="button" className="sp-quick" onClick={() => openNew("login")}>
                  <strong>+</strong>
                  <span>New login</span>
                </button>
                <button type="button" className="sp-quick" onClick={() => setNav("tools")}>
                  <strong>✦</strong>
                  <span>Generator</span>
                </button>
              </div>
              <p className="sp-section-title">Favorites</p>
              {favorites.length === 0 ? (
                <div className="sp-empty">Star items to see them here.</div>
              ) : (
                <div className="sp-list">
                  {favorites.map((c) => (
                    <ItemRow
                      key={c.id}
                      c={c}
                      onOpen={() => setSelected(c)}
                      onCopy={onCopy}
                    />
                  ))}
                </div>
              )}
              <p className="sp-section-title" style={{ marginTop: "1.25rem" }}>
                Recent logins
              </p>
              <div className="sp-list">
                {vault.ciphers
                  .filter((c) => c.type === 1)
                  .slice(0, 12)
                  .map((c) => (
                    <ItemRow
                      key={c.id}
                      c={c}
                      onOpen={() => setSelected(c)}
                      onCopy={onCopy}
                    />
                  ))}
              </div>
            </>
          ) : null}

          {nav === "vault" ? (
            <>
              <div className="sp-search-wrap">
                <input
                  className="sp-search"
                  placeholder="Search vault"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
              <div className="sp-chips">
                {(
                  [
                    ["all", `All (${counts.all})`],
                    ["login", `Passwords (${counts.login})`],
                    ["note", `Notes (${counts.note})`],
                    ["card", `Cards (${counts.card})`],
                    ["identity", `IDs (${counts.identity})`],
                    ["fav", `★ (${counts.fav})`],
                    ["trash", `Trash (${counts.trash})`],
                  ] as const
                ).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className={`sp-chip ${filter === id ? "active" : ""}`}
                    onClick={() => setFilter(id)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              {items.length === 0 ? (
                <div className="sp-empty">Nothing here yet.</div>
              ) : (
                <div className="sp-list">
                  {items.map((c) => (
                    <ItemRow
                      key={c.id}
                      c={c}
                      trash={filter === "trash"}
                      onOpen={() => setSelected(c)}
                      onCopy={onCopy}
                    />
                  ))}
                </div>
              )}
            </>
          ) : null}

          {nav === "tools" ? (
            <ToolsPanel
              show={show}
              onChange={onChange}
              setError={setError}
              onOpenCipher={setSelected}
            />
          ) : null}

          {nav === "more" ? (
            <MorePanel
              busy={busy}
              onSync={syncNow}
              onLock={async () => {
                vault.lock();
                onChange();
              }}
              onLogout={async () => {
                await clearDeviceUnlock();
                await clearAutofillCache();
                vault.logout();
                onChange();
              }}
              onChange={onChange}
              setError={setError}
              show={show}
            />
          ) : null}
        </main>

        {(nav === "home" || nav === "vault") && filter !== "trash" ? (
          <button
            type="button"
            className="sp-fab"
            aria-label="Add item"
            onClick={() => setShowAddMenu(true)}
          >
            +
          </button>
        ) : null}

        <nav className="sp-nav">
          {(
            [
              ["home", "⌂", "Home"],
              ["vault", "▣", "Vault"],
              ["tools", "✦", "Tools"],
              ["more", "⋯", "More"],
            ] as const
          ).map(([id, ico, label]) => (
            <button
              key={id}
              type="button"
              className={nav === id ? "active" : ""}
              onClick={() => setNav(id)}
            >
              <span className="nav-ico">{ico}</span>
              {label}
            </button>
          ))}
        </nav>
      </div>

      {toast ? <div className="sp-toast">{toast}</div> : null}

      {pendingSave ? (
        <PendingSavePrompt
          pending={pendingSave}
          show={show}
          setError={setError}
          onOpen={(id) => {
            const c = vault.ciphers.find((x) => x.id === id);
            if (c) setSelected(c);
          }}
          onDone={() => setPendingSave(null)}
        />
      ) : offerAutofill ? (
        <AutofillSetupPrompt
          show={show}
          onDone={() => setOfferAutofill(false)}
        />
      ) : offerQuick ? (
        <QuickUnlockSetup
          show={show}
          onDone={() => {
            sessionStorage.removeItem("ciphernest.offerQuickUnlock");
            setOfferQuick(false);
          }}
        />
      ) : null}

      {showAddMenu ? (
        <div className="sp-drawer" onClick={() => setShowAddMenu(false)}>
          <div className="sp-drawer-panel" onClick={(e) => e.stopPropagation()}>
            <div className="sp-sheet-head">
              <h2 className="brand">Add to vault</h2>
              <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={() => setShowAddMenu(false)}>
                Close
              </button>
            </div>
            <div className="sp-type-pick">
              <button type="button" onClick={() => openNew("login")}>
                <strong>Password</strong>
                <div className="sp-sub" style={{ margin: "0.25rem 0 0" }}>
                  Website login with username & password
                </div>
              </button>
              <button type="button" onClick={() => openNew("note")}>
                <strong>Secure note</strong>
                <div className="sp-sub" style={{ margin: "0.25rem 0 0" }}>
                  Encrypted freeform note
                </div>
              </button>
              <button type="button" onClick={() => openNew("card")}>
                <strong>Payment card</strong>
                <div className="sp-sub" style={{ margin: "0.25rem 0 0" }}>
                  Card number, expiry, CVV
                </div>
              </button>
              <button type="button" onClick={() => openNew("identity")}>
                <strong>Identity</strong>
                <div className="sp-sub" style={{ margin: "0.25rem 0 0" }}>
                  Name, email, address, phone
                </div>
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {selected ? (
        <CipherSheet
          cipher={selected}
          isTrash={filter === "trash" && Boolean(selected.id)}
          onClose={() => {
            setSelected(null);
            setNewKind(null);
          }}
          onSaved={() => {
            setSelected(null);
            setNewKind(null);
            onChange();
          }}
          setError={setError}
          show={show}
          forceEdit={!selected.id || Boolean(newKind)}
        />
      ) : null}
    </div>
  );
}

function ItemRow({
  c,
  onOpen,
  onCopy,
  trash,
}: {
  c: VaultCipher;
  onOpen: () => void;
  onCopy: (
    label: string,
    value: string,
    e?: React.MouseEvent,
    item?: VaultCipher
  ) => void;
  trash?: boolean;
}) {
  return (
    <div className="sp-item" onClick={onOpen} role="button" tabIndex={0} onKeyDown={(e) => e.key === "Enter" && onOpen()}>
      <div className={`sp-avatar ${avatarClass(c.type)}`}>{initials(c.name)}</div>
      <div className="sp-item-body">
        <strong>
          {c.favorite ? "★ " : ""}
          {c.name}
        </strong>
        <span>{subtitle(c)}</span>
      </div>
      {!trash && c.type === 1 && c.login?.password ? (
        <div className="sp-item-actions">
          {c.login.username ? (
            <button
              type="button"
              className="sp-icon-btn"
              title="Copy username"
              onClick={(e) => onCopy("Username", c.login!.username!, e, c)}
            >
              @
            </button>
          ) : null}
          <button
            type="button"
            className="sp-icon-btn"
            title="Copy password"
            onClick={(e) => onCopy("Password", c.login!.password!, e, c)}
          >
            ⎘
          </button>
        </div>
      ) : (
        <span />
      )}
    </div>
  );
}

function ToolsPanel({
  show,
  onChange,
  setError,
  onOpenCipher,
}: {
  show: (m: string) => void;
  onChange: () => void;
  setError: (s: string | null) => void;
  onOpenCipher: (c: VaultCipher) => void;
}) {
  const [value, setValue] = useState("");
  const [length, setLength] = useState(20);
  const [upper, setUpper] = useState(true);
  const [lower, setLower] = useState(true);
  const [digits, setDigits] = useState(true);
  const [special, setSpecial] = useState(true);
  const [folderName, setFolderName] = useState("");
  const [sendName, setSendName] = useState("Secure note");
  const [sendText, setSendText] = useState("");
  const [sendPw, setSendPw] = useState("");
  const [sendLink, setSendLink] = useState("");
  const [sends, setSends] = useState<Array<Record<string, unknown>>>([]);
  const [healthTab, setHealthTab] = useState<
    "weak" | "reused" | "old" | "noTotp"
  >("weak");

  const health = useMemo(
    () => analyzePasswordHealth(vault.ciphers),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vault.ciphers]
  );

  useEffect(() => {
    void vault.listSends().then((r) => setSends(r.data || [])).catch(() => setSends([]));
  }, []);

  const healthList =
    healthTab === "weak"
      ? health.weak
      : healthTab === "reused"
        ? health.reused
        : healthTab === "old"
          ? health.old
          : health.noTotp;

  return (
    <div>
      <p className="sp-section-title">Password health</p>
      <p className="sp-sub">
        Score {health.score}/100 · {health.totalLogins} logins checked for weak,
        reused, old, and missing authenticator codes.
      </p>
      <div className="sp-health-score">{health.score}</div>
      <div className="sp-chip-row">
        {(
          [
            ["weak", `Weak (${health.weak.length})`],
            ["reused", `Reused (${health.reused.length})`],
            ["old", `Old (${health.old.length})`],
            ["noTotp", `No 2FA (${health.noTotp.length})`],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`sp-chip ${healthTab === id ? "active" : ""}`}
            onClick={() => setHealthTab(id)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="sp-list" style={{ marginBottom: "1.5rem" }}>
        {healthList.length === 0 ? (
          <p className="sp-sub">Nothing in this category.</p>
        ) : (
          healthList.slice(0, 40).map((issue) => {
            const cipher = vault.ciphers.find((c) => c.id === issue.cipherId);
            return (
              <button
                key={`${issue.kind}-${issue.cipherId}`}
                type="button"
                className="sp-item"
                onClick={() => cipher && onOpenCipher(cipher)}
              >
                <div className={`sp-avatar ${avatarClass(1)}`}>
                  {initials(issue.name)}
                </div>
                <div className="sp-item-body">
                  <strong>{issue.name}</strong>
                  <span>{issue.detail}</span>
                </div>
                <span />
              </button>
            );
          })
        )}
      </div>

      <p className="sp-section-title">Password generator</p>
      <div className="sp-gen-out">{value || "Tap generate"}</div>
      <div className="sp-field">
        <label>Length · {length}</label>
        <input
          type="range"
          min={8}
          max={64}
          value={length}
          onChange={(e) => setLength(Number(e.target.value))}
        />
      </div>
      <div className="sp-toggle-row">
        <label className="sp-check">
          <input type="checkbox" checked={upper} onChange={(e) => setUpper(e.target.checked)} />
          A–Z
        </label>
        <label className="sp-check">
          <input type="checkbox" checked={lower} onChange={(e) => setLower(e.target.checked)} />
          a–z
        </label>
        <label className="sp-check">
          <input type="checkbox" checked={digits} onChange={(e) => setDigits(e.target.checked)} />
          0–9
        </label>
        <label className="sp-check">
          <input type="checkbox" checked={special} onChange={(e) => setSpecial(e.target.checked)} />
          Symbols
        </label>
      </div>
      <div style={{ display: "flex", gap: "0.6rem", flexWrap: "wrap" }}>
        <button
          type="button"
          className="sp-btn"
          onClick={async () =>
            setValue(
              await vault.generatePassword(length, { upper, lower, digits, special })
            )
          }
        >
          Generate
        </button>
        <button
          type="button"
          className="sp-btn sp-btn-ghost"
          disabled={!value}
          onClick={async () => {
            await copyText(value);
            show("Password copied");
          }}
        >
          Copy
        </button>
      </div>

      <p className="sp-section-title" style={{ marginTop: "2rem" }}>
        Folders
      </p>
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.85rem" }}>
        <input
          className="sp-input"
          placeholder="New folder"
          value={folderName}
          onChange={(e) => setFolderName(e.target.value)}
        />
        <button
          type="button"
          className="sp-btn"
          onClick={async () => {
            try {
              await vault.createFolder(folderName.trim());
              setFolderName("");
              onChange();
              show("Folder created");
            } catch (err) {
              setError(err instanceof Error ? err.message : "Failed");
            }
          }}
        >
          Add
        </button>
      </div>
      <div className="sp-list">
        {vault.folders.map((f) => (
          <div key={f.id} className="sp-item" style={{ cursor: "default" }}>
            <div className="sp-avatar">F</div>
            <div className="sp-item-body">
              <strong>{f.name}</strong>
              <span>
                {vault.ciphers.filter((c) => c.folderId === f.id).length} items
              </span>
            </div>
            <span />
          </div>
        ))}
      </div>

      <p className="sp-section-title" style={{ marginTop: "2rem" }}>
        Sends
      </p>
      <p className="sp-sub">Share encrypted text via a link. Optional password.</p>
      <div className="sp-field">
        <label>Name</label>
        <input value={sendName} onChange={(e) => setSendName(e.target.value)} />
      </div>
      <div className="sp-field">
        <label>Text</label>
        <textarea
          className="sp-input"
          rows={3}
          value={sendText}
          onChange={(e) => setSendText(e.target.value)}
        />
      </div>
      <div className="sp-field">
        <label>Access password (optional)</label>
        <input
          type="password"
          value={sendPw}
          onChange={(e) => setSendPw(e.target.value)}
        />
      </div>
      <button
        type="button"
        className="sp-btn"
        onClick={async () => {
          try {
            const res = await vault.createTextSend({
              name: sendName || "Send",
              text: sendText,
              password: sendPw || undefined,
            });
            setSendLink(res.accessUrl);
            setSendText("");
            const list = await vault.listSends();
            setSends(list.data || []);
            show("Send created");
          } catch (err) {
            setError(err instanceof Error ? err.message : "Failed");
          }
        }}
      >
        Create send
      </button>
      {sendLink ? (
        <div className="sp-copy-row" style={{ marginTop: "0.75rem" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <label>Access link</label>
            <div className="val" style={{ wordBreak: "break-all" }}>
              {sendLink}
            </div>
          </div>
          <button
            type="button"
            className="sp-icon-btn"
            onClick={() => {
              void copyText(sendLink, { sensitive: false });
              show("Link copied");
            }}
          >
            ⎘
          </button>
        </div>
      ) : null}
      <div className="sp-list" style={{ marginTop: "0.75rem" }}>
        {sends.map((s) => (
          <div key={String(s.id)} className="sp-item" style={{ cursor: "default" }}>
            <div className="sp-avatar">S</div>
            <div className="sp-item-body">
              <strong>{String(s.id).slice(0, 8)}…</strong>
              <span>
                accesses {String(s.accessCount ?? 0)} · deletes{" "}
                {String(s.deletionDate || "").slice(0, 10)}
              </span>
            </div>
            <button
              type="button"
              className="sp-icon-btn"
              onClick={async () => {
                try {
                  await vault.deleteSend(String(s.id));
                  setSends((prev) => prev.filter((x) => x.id !== s.id));
                  show("Send deleted");
                } catch (err) {
                  setError(err instanceof Error ? err.message : "Failed");
                }
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function MorePanel({
  busy,
  onSync,
  onLock,
  onLogout,
  onChange,
  setError,
  show,
}: {
  busy: boolean;
  onSync: () => void;
  onLock: () => void;
  onLogout: () => void;
  onChange: () => void;
  setError: (s: string | null) => void;
  show: (m: string) => void;
}) {
  const orgs =
    ((vault.profile?.organizations as Array<Record<string, unknown>>) ||
      []) as Array<Record<string, unknown>>;
  const [inviteEmail, setInviteEmail] = useState("");
  const [orgName, setOrgName] = useState("Family");
  const [selectedOrg, setSelectedOrg] = useState("");
  const [members, setMembers] = useState<Array<Record<string, unknown>>>([]);
  const [pinOn, setPinOn] = useState(false);
  const [bioOn, setBioOn] = useState(false);
  const [bioOk, setBioOk] = useState(false);
  const [autofillOn, setAutofillOn] = useState(false);
  const [pin1, setPin1] = useState("");
  const [pin2, setPin2] = useState("");
  const [sec, setSec] = useState<SecuritySettings | null>(null);
  const [mpCurrent, setMpCurrent] = useState("");
  const [mpNext, setMpNext] = useState("");
  const [mpConfirm, setMpConfirm] = useState("");
  const [mpBusy, setMpBusy] = useState(false);
  const native = isNativeApp();

  useEffect(() => {
    let cancelled = false;
    async function refresh() {
      setPinOn(await isPinEnabled());
      setBioOn(await isBioEnabled());
      setBioOk(await biometricAvailable());
      setSec(await loadSecuritySettings());
      if (isNativeApp()) {
        const { enabled } = await isAutofillServiceEnabled();
        if (!cancelled) setAutofillOn(enabled);
      }
    }
    void refresh();
    if (!isNativeApp()) return;
    const sub = CapApp.addListener("appStateChange", ({ isActive }) => {
      if (isActive) void refresh();
    });
    return () => {
      cancelled = true;
      void sub.then((h) => h.remove());
    };
  }, []);

  async function patchSec(partial: Partial<SecuritySettings>) {
    const next = { ...(sec || (await loadSecuritySettings())), ...partial };
    setSec(next);
    await saveSecuritySettings(next);
    show("Security settings saved");
  }

  return (
    <div>
      <div className="sp-settings-block">
        <h3>Account</h3>
        <p className="sp-sub" style={{ marginBottom: "0.75rem" }}>
          {vault.session?.email}
        </p>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button type="button" className="sp-btn sp-btn-ghost" disabled={busy} onClick={onSync}>
            Sync now
          </button>
          <button type="button" className="sp-btn sp-btn-ghost" onClick={onLock}>
            Lock
          </button>
          <button type="button" className="sp-btn sp-btn-danger" onClick={onLogout}>
            Log out
          </button>
        </div>
        <p className="sp-section-title" style={{ marginTop: "1.25rem" }}>
          Master password
        </p>
        <p className="sp-sub">
          Changing it rewraps your vault key. PIN / fingerprint must be set up
          again afterward. Download a recovery kit before you change it.
        </p>
        <button
          type="button"
          className="sp-btn sp-btn-ghost"
          style={{ marginBottom: "0.75rem" }}
          onClick={async () => {
            try {
              const text = buildRecoveryKit({ email: vault.session?.email || "" });
              await downloadText(
                `ciphernest-emergency-kit-${Date.now()}.txt`,
                text,
                "text/plain"
              );
              show("Recovery kit ready");
            } catch (err) {
              setError(err instanceof Error ? err.message : "Export failed");
            }
          }}
        >
          Download recovery kit
        </button>
        <PasswordField
          id="mp-current"
          label="Current master password"
          value={mpCurrent}
          onChange={setMpCurrent}
          autoComplete="current-password"
        />
        <PasswordField
          id="mp-next"
          label="New master password"
          value={mpNext}
          onChange={setMpNext}
          autoComplete="new-password"
        />
        <PasswordField
          id="mp-confirm"
          label="Confirm new master password"
          value={mpConfirm}
          onChange={setMpConfirm}
          autoComplete="new-password"
        />
        <button
          type="button"
          className="sp-btn"
          disabled={mpBusy || !mpCurrent || !mpNext}
          onClick={async () => {
            setMpBusy(true);
            setError(null);
            try {
              if (mpNext !== mpConfirm) throw new Error("New passwords do not match");
              await vault.changeMasterPassword(mpCurrent, mpNext);
              await clearDeviceUnlock();
              setPinOn(false);
              setBioOn(false);
              setMpCurrent("");
              setMpNext("");
              setMpConfirm("");
              show("Master password updated — set up PIN/fingerprint again");
            } catch (err) {
              setError(err instanceof Error ? err.message : "Change failed");
            } finally {
              setMpBusy(false);
            }
          }}
        >
          {mpBusy ? "Updating…" : "Change master password"}
        </button>
      </div>

      <div className="sp-settings-block">
        <h3>Quick unlock</h3>
        <p className="sp-sub">
          After unlocking once with your master password, use a PIN and/or
          fingerprint. Your master password is never stored.
          {!native ? " Fingerprint requires the Android app." : ""}
        </p>
        {pinOn ? (
          <button
            type="button"
            className="sp-btn sp-btn-ghost"
            style={{ marginBottom: "0.75rem" }}
            onClick={async () => {
              await disablePinUnlock();
              setPinOn(false);
              show("PIN unlock disabled");
            }}
          >
            Disable PIN
          </button>
        ) : (
          <div style={{ marginBottom: "0.85rem" }}>
            <div className="sp-field">
              <label>New PIN (4–8 digits)</label>
              <input
                inputMode="numeric"
                pattern="[0-9]*"
                value={pin1}
                onChange={(e) => setPin1(e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder="••••"
              />
            </div>
            <div className="sp-field">
              <label>Confirm PIN</label>
              <input
                inputMode="numeric"
                pattern="[0-9]*"
                value={pin2}
                onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder="••••"
              />
            </div>
            <button
              type="button"
              className="sp-btn"
              onClick={async () => {
                try {
                  if (pin1 !== pin2) throw new Error("PINs do not match");
                  const snap = vault.getUnlockSnapshot();
                  if (!snap) throw new Error("Vault is locked");
                  await enablePinUnlock(pin1, snap);
                  setPin1("");
                  setPin2("");
                  setPinOn(true);
                  show("PIN unlock enabled");
                } catch (err) {
                  setError(err instanceof Error ? err.message : "Failed");
                }
              }}
            >
              Enable PIN unlock
            </button>
          </div>
        )}
        {bioOn ? (
          <button
            type="button"
            className="sp-btn sp-btn-ghost"
            onClick={async () => {
              await disableBiometricUnlock();
              setBioOn(false);
              show("Fingerprint unlock disabled");
            }}
          >
            Disable fingerprint
          </button>
        ) : (
          <button
            type="button"
            className="sp-btn"
            disabled={!native || !bioOk}
            onClick={async () => {
              try {
                const snap = vault.getUnlockSnapshot();
                if (!snap) throw new Error("Vault is locked");
                await enableBiometricUnlock(snap);
                setBioOn(true);
                show("Fingerprint unlock enabled");
              } catch (err) {
                setError(err instanceof Error ? err.message : "Failed");
              }
            }}
          >
            {native
              ? bioOk
                ? "Enable fingerprint unlock"
                : "Fingerprint unavailable"
              : "Fingerprint (Android app only)"}
          </button>
        )}
      </div>

      {sec ? (
        <div className="sp-settings-block">
          <h3>Security</h3>
          <p className="sp-sub">
            Vault UI locks after idle time (default 5 minutes) or when you close
            the app fully. Minimizing alone does not lock. Autofill keeps working
            for 14 days after unlock — even while the vault is locked — until you
            log out.
          </p>
          <label className="sp-check">
            <input
              type="checkbox"
              checked={sec.lockOnBackground}
              onChange={(e) => void patchSec({ lockOnBackground: e.target.checked })}
            />
            Lock immediately when minimized (not recommended)
          </label>
          <div className="sp-field" style={{ marginTop: "0.65rem" }}>
            <label>Lock after inactivity</label>
            <select
              value={String(sec.lockTimeoutMin)}
              onChange={(e) => void patchSec({ lockTimeoutMin: Number(e.target.value) })}
            >
              <option value="0">Off (manual lock / full close only)</option>
              <option value="1">1 minute</option>
              <option value="5">5 minutes</option>
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
            </select>
          </div>
          <label className="sp-check">
            <input
              type="checkbox"
              checked={sec.blockScreenshots}
              onChange={(e) => void patchSec({ blockScreenshots: e.target.checked })}
            />
            Block screenshots &amp; app switcher previews
          </label>
          <label className="sp-check">
            <input
              type="checkbox"
              checked={sec.notifyOnLock}
              onChange={(e) => void patchSec({ notifyOnLock: e.target.checked })}
            />
            Notify when vault locks
          </label>
          <div className="sp-field" style={{ marginTop: "0.65rem" }}>
            <label>Clear clipboard after</label>
            <select
              value={String(sec.clipboardClearSec)}
              onChange={(e) =>
                void patchSec({ clipboardClearSec: Number(e.target.value) })
              }
            >
              <option value="0">Never</option>
              <option value="15">15 seconds</option>
              <option value="30">30 seconds</option>
              <option value="60">60 seconds</option>
            </select>
          </div>
          {native ? (
            <button
              type="button"
              className="sp-btn"
              style={{ marginTop: "0.85rem" }}
              onClick={async () => {
                try {
                  await openAutofillSettings();
                  show(
                    autofillOn
                      ? "Change autofill service in system settings"
                      : "Enable CipherNest as your autofill service"
                  );
                } catch (err) {
                  setError(err instanceof Error ? err.message : "Failed");
                }
              }}
            >
              {autofillOn ? "Change Android autofill" : "Set up Android autofill"}
            </button>
          ) : (
            <p className="sp-sub" style={{ marginTop: "0.75rem" }}>
              Autofill setup is in the Android app.
            </p>
          )}
          {native ? (
            <p className="sp-sub" style={{ marginTop: "0.45rem" }}>
              {autofillOn
                ? "CipherNest is your autofill service."
                : "Autofill is not set to CipherNest yet."}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="sp-settings-block">
        <h3>Import &amp; export</h3>
        <p className="sp-sub">
          Import CipherNest vault JSON or CSV. Export is a local plaintext
          backup — store it safely.
        </p>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <label className="sp-btn" style={{ cursor: "pointer" }}>
            Import file
            <input
              type="file"
              accept=".json,.csv,text/csv,application/json"
              style={{ display: "none" }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                try {
                  const text = await file.text();
                  const items = parseImportFile(text, file.name);
                  const n = await vault.importPlainItems(items);
                  onChange();
                  show(`Imported ${n} items`);
                } catch (err) {
                  setError(err instanceof Error ? err.message : "Import failed");
                }
              }}
            />
          </label>
          <button
            type="button"
            className="sp-btn sp-btn-ghost"
              onClick={() => {
                void downloadText(
                  "ciphernest-export.json",
                  vault.exportVaultJson(),
                  "application/json"
                );
                show("JSON exported");
              }}
            >
              Export JSON
            </button>
            <button
              type="button"
              className="sp-btn sp-btn-ghost"
              onClick={() => {
                void downloadText(
                  "ciphernest-export.csv",
                  vault.exportVaultCsv(),
                  "text/csv"
                );
                show("CSV exported");
              }}
            >
              Export CSV
            </button>
        </div>
      </div>

      <TwoFactorSettings setError={setError} show={show} />
      <PasskeySettings setError={setError} show={show} />

      <div className="sp-settings-block">
        <h3>Family sharing</h3>
        <p className="sp-sub">
          Invite a registered account, then confirm so they get the org key.
        </p>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
          <input
            className="sp-input"
            style={{ flex: 1 }}
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            placeholder="Org name"
          />
          <button
            type="button"
            className="sp-btn"
            onClick={async () => {
              try {
                await vault.createOrganization(orgName.trim() || "Family");
                onChange();
                show("Organization created");
              } catch (err) {
                setError(err instanceof Error ? err.message : "Failed");
              }
            }}
          >
            Create
          </button>
        </div>
        <div className="sp-list">
          {orgs.map((o) => (
            <div
              key={String(o.id)}
              className="sp-item"
              onClick={async () => {
                const id = String(o.id);
                setSelectedOrg(id);
                try {
                  const res = await vault.listOrgMembers(id);
                  setMembers(res.data || []);
                } catch (err) {
                  setError(err instanceof Error ? err.message : "Failed");
                }
              }}
            >
              <div className="sp-avatar">F</div>
              <div className="sp-item-body">
                <strong>{String(o.name)}</strong>
                <span>
                  status {String(o.status)} · type {String(o.type)}
                </span>
              </div>
              <span />
            </div>
          ))}
        </div>
        {selectedOrg ? (
          <>
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.85rem" }}>
              <input
                className="sp-input"
                placeholder="Invite email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
              <button
                type="button"
                className="sp-btn"
                onClick={async () => {
                  try {
                    await vault.inviteToOrg(selectedOrg, inviteEmail);
                    setInviteEmail("");
                    const res = await vault.listOrgMembers(selectedOrg);
                    setMembers(res.data || []);
                    show("Invited");
                  } catch (err) {
                    setError(err instanceof Error ? err.message : "Invite failed");
                  }
                }}
              >
                Invite
              </button>
            </div>
            <div className="sp-list" style={{ marginTop: "0.75rem" }}>
              {members.map((m) => (
                <div key={String(m.id)} className="sp-item" style={{ cursor: "default" }}>
                  <div className="sp-avatar">U</div>
                  <div className="sp-item-body">
                    <strong>{String(m.email || m.id)}</strong>
                    <span>status {String(m.status)}</span>
                  </div>
                  {Number(m.status) === 1 ? (
                    <button
                      type="button"
                      className="sp-btn sp-btn-sm"
                      onClick={async () => {
                        try {
                          await vault.confirmOrgMember(selectedOrg, String(m.id));
                          const res = await vault.listOrgMembers(selectedOrg);
                          setMembers(res.data || []);
                          show("Confirmed");
                        } catch (err) {
                          setError(
                            err instanceof Error ? err.message : "Confirm failed"
                          );
                        }
                      }}
                    >
                      Confirm
                    </button>
                  ) : (
                    <span />
                  )}
                </div>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function TwoFactorSettings({
  setError,
  show,
}: {
  setError: (s: string | null) => void;
  show: (m: string) => void;
}) {
  const enabled = Boolean(vault.profile?.twoFactorEnabled);
  const [secret, setSecret] = useState("");
  const [otpauth, setOtpauth] = useState("");
  const [code, setCode] = useState("");
  const [mp, setMp] = useState("");
  const [busy, setBusy] = useState(false);

  return (
    <div className="sp-settings-block">
      <h3>Account 2FA</h3>
      <p className="sp-sub">
        Require an authenticator code with your master password at unlock.
        {enabled ? " Currently enabled." : ""}
      </p>
      {enabled ? (
        <div>
          <div className="sp-field">
            <label>Master password to disable</label>
            <input
              type="password"
              value={mp}
              onChange={(e) => setMp(e.target.value)}
            />
          </div>
          <button
            type="button"
            className="sp-btn sp-btn-danger"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              try {
                await vault.disableAccountTwoFactor(mp);
                setMp("");
                show("2FA disabled");
              } catch (err) {
                setError(err instanceof Error ? err.message : "Failed");
              } finally {
                setBusy(false);
              }
            }}
          >
            Disable 2FA
          </button>
        </div>
      ) : (
        <div>
          {!secret ? (
            <button
              type="button"
              className="sp-btn"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const res = await vault.setupAccountTwoFactor();
                  setSecret(res.secret);
                  setOtpauth(res.otpauth);
                } catch (err) {
                  setError(err instanceof Error ? err.message : "Failed");
                } finally {
                  setBusy(false);
                }
              }}
            >
              Set up authenticator
            </button>
          ) : (
            <>
              <p className="sp-sub" style={{ wordBreak: "break-all" }}>
                Secret: <code>{secret}</code>
                <br />
                Add to your authenticator app, then confirm a code.
              </p>
              <div className="sp-field">
                <label>Code</label>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                />
              </div>
              <div className="sp-field">
                <label>Master password</label>
                <input
                  type="password"
                  value={mp}
                  onChange={(e) => setMp(e.target.value)}
                />
              </div>
              <button
                type="button"
                className="sp-btn"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await vault.enableAccountTwoFactor(secret, code, mp);
                    setSecret("");
                    setOtpauth("");
                    setCode("");
                    setMp("");
                    show("2FA enabled");
                  } catch (err) {
                    setError(err instanceof Error ? err.message : "Failed");
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Confirm &amp; enable
              </button>
              {otpauth ? (
                <p className="sp-sub" style={{ marginTop: "0.5rem", fontSize: "0.75rem" }}>
                  {otpauth}
                </p>
              ) : null}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function PasskeySettings({
  setError,
  show,
}: {
  setError: (s: string | null) => void;
  show: (m: string) => void;
}) {
  const [on, setOn] = useState(false);
  const available = webauthnAvailable();

  useEffect(() => {
    void hasPasskey().then(setOn);
  }, []);

  return (
    <div className="sp-settings-block">
      <h3>Passkey confirm</h3>
      <p className="sp-sub">
        Use Windows Hello / Face ID / fingerprint instead of typing your master
        password when opening protected items.
      </p>
      {!available ? (
        <p className="sp-sub">Passkeys are not available in this browser.</p>
      ) : on ? (
        <button
          type="button"
          className="sp-btn sp-btn-ghost"
          onClick={async () => {
            await clearPasskey();
            setOn(false);
            show("Passkey removed");
          }}
        >
          Remove passkey
        </button>
      ) : (
        <button
          type="button"
          className="sp-btn"
          onClick={async () => {
            try {
              const email = vault.session?.email;
              if (!email) throw new Error("Locked");
              await registerPasskey(email);
              setOn(true);
              show("Passkey registered");
            } catch (err) {
              setError(err instanceof Error ? err.message : "Failed");
            }
          }}
        >
          Register device passkey
        </button>
      )}
    </div>
  );
}

function CipherSheet({
  cipher,
  isTrash,
  onClose,
  onSaved,
  setError,
  show,
  forceEdit,
}: {
  cipher: VaultCipher;
  isTrash?: boolean;
  onClose: () => void;
  onSaved: () => void;
  setError: (s: string | null) => void;
  show: (m: string) => void;
  forceEdit?: boolean;
}) {
  const [editing, setEditing] = useState(Boolean(forceEdit));
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState(cipher.name.startsWith("[") ? "" : cipher.name);
  const [username, setUsername] = useState(cipher.login?.username || "");
  const [password, setPassword] = useState(cipher.login?.password || "");
  const [uri, setUri] = useState(cipher.login?.uris?.[0]?.uri || "");
  const [totp, setTotp] = useState(cipher.login?.totp || "");
  const [notes, setNotes] = useState(cipher.notes || "");
  const [folderId, setFolderId] = useState(cipher.folderId || "");
  const [favorite, setFavorite] = useState(cipher.favorite);
  const [reprompt, setReprompt] = useState(cipher.reprompt === 1);
  const [unlockedSensitive, setUnlockedSensitive] = useState(cipher.reprompt !== 1);
  const [attachments, setAttachments] = useState<
    Array<Record<string, unknown>>
  >([]);
  const [cardholderName, setCardholderName] = useState(
    cipher.card?.cardholderName || ""
  );
  const [brand, setBrand] = useState(cipher.card?.brand || "");
  const [number, setNumber] = useState(cipher.card?.number || "");
  const [expMonth, setExpMonth] = useState(cipher.card?.expMonth || "");
  const [expYear, setExpYear] = useState(cipher.card?.expYear || "");
  const [code, setCode] = useState(cipher.card?.code || "");
  const [firstName, setFirstName] = useState(cipher.identity?.firstName || "");
  const [lastName, setLastName] = useState(cipher.identity?.lastName || "");
  const [email, setEmail] = useState(cipher.identity?.email || "");
  const [phone, setPhone] = useState(cipher.identity?.phone || "");
  const [address1, setAddress1] = useState(cipher.identity?.address1 || "");
  const [city, setCity] = useState(cipher.identity?.city || "");
  const [showPass, setShowPass] = useState(false);

  useEffect(() => {
    if (!cipher.id) return;
    void vault
      .listAttachments(cipher.id)
      .then((r) => setAttachments(r.data || []))
      .catch(() => setAttachments([]));
  }, [cipher.id]);

  async function ensureSensitive(): Promise<boolean> {
    if (unlockedSensitive) return true;
    if (await assertPasskey()) {
      setUnlockedSensitive(true);
      return true;
    }
    const mp = window.prompt("Enter master password to view");
    if (!mp || !(await vault.verifyMasterPassword(mp))) {
      setError("Master password required");
      return false;
    }
    setUnlockedSensitive(true);
    return true;
  }

  async function save() {
    setBusy(true);
    setError(null);
    try {
      if (cipher.type === 1) {
        await vault.saveLogin({
          id: cipher.id || undefined,
          name: name || "Untitled",
          username,
          password,
          notes: notes || undefined,
          folderId: folderId || null,
          uri: uri || null,
          totp: totp || null,
          favorite,
          reprompt: reprompt ? 1 : 0,
        });
      } else if (cipher.type === 2) {
        await vault.saveSecureNote({
          id: cipher.id || undefined,
          name: name || "Untitled note",
          notes,
          folderId: folderId || null,
          favorite,
        });
      } else if (cipher.type === 3) {
        await vault.saveCard({
          id: cipher.id || undefined,
          name: name || "Card",
          cardholderName,
          brand,
          number,
          expMonth,
          expYear,
          code,
          notes: notes || undefined,
          folderId: folderId || null,
          favorite,
        });
      } else if (cipher.type === 4) {
        await vault.saveIdentity({
          id: cipher.id || undefined,
          name: name || "Identity",
          firstName,
          lastName,
          email,
          phone,
          address1,
          city,
          notes: notes || undefined,
          folderId: folderId || null,
          favorite,
        });
      }
      show("Saved");
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!cipher.id) return onClose();
    if (!confirm("Move this item to trash?")) return;
    setBusy(true);
    try {
      await vault.deleteCipher(cipher.id);
      show("Moved to trash");
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  async function restore() {
    if (!cipher.id) return;
    setBusy(true);
    try {
      await vault.restoreCipher(cipher.id);
      show("Restored");
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Restore failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sp-drawer" onClick={onClose}>
      <div className="sp-drawer-panel" onClick={(e) => e.stopPropagation()} role="dialog">
        <div className="sp-sheet-head">
          <div>
            <h2 className="brand">{cipher.id ? cipher.name || "Item" : "New item"}</h2>
            <p className="sp-sub" style={{ margin: "0.2rem 0 0" }}>
              {cipher.type === 1
                ? "Password"
                : cipher.type === 2
                  ? "Secure note"
                  : cipher.type === 3
                    ? "Card"
                    : "Identity"}
            </p>
          </div>
          <button type="button" className="sp-btn sp-btn-ghost sp-btn-sm" onClick={onClose}>
            Close
          </button>
        </div>

        {isTrash ? (
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button type="button" className="sp-btn" disabled={busy} onClick={restore}>
              Restore
            </button>
            <button type="button" className="sp-btn sp-btn-ghost" onClick={onClose}>
              Close
            </button>
          </div>
        ) : !editing ? (
          <>
            {cipher.type === 1 ? (
              <>
                <CopyField
                  label="Username"
                  value={cipher.login?.username || ""}
                  onCopy={() => {
                    void copyText(cipher.login?.username || "");
                    show("Username copied");
                  }}
                />
                <CopyField
                  label="Password"
                  value={
                    !unlockedSensitive
                      ? "••••••••••••"
                      : showPass
                        ? cipher.login?.password || ""
                        : "••••••••••••"
                  }
                  raw={unlockedSensitive ? cipher.login?.password || "" : ""}
                  onCopy={async () => {
                    if (!(await ensureSensitive())) return;
                    void copyText(cipher.login?.password || "");
                    show("Password copied");
                  }}
                  extra={
                    <button
                      type="button"
                      className="sp-icon-btn"
                      onClick={async () => {
                        if (!(await ensureSensitive())) return;
                        setShowPass((s) => !s);
                      }}
                    >
                      {showPass ? "Hide" : "Show"}
                    </button>
                  }
                />
                {cipher.login?.uris?.[0]?.uri ? (
                  <CopyField
                    label="Website"
                    value={cipher.login.uris[0].uri}
                    onCopy={() => {
                      void copyText(cipher.login!.uris[0]!.uri || "");
                      show("URL copied");
                    }}
                    extra={
                      <button
                        type="button"
                        className="sp-icon-btn"
                        onClick={() =>
                          window.open(cipher.login!.uris[0]!.uri!, "_blank")
                        }
                      >
                        ↗
                      </button>
                    }
                  />
                ) : null}
                {cipher.login?.totp ? (
                  <LiveTotpField
                    secret={cipher.login.totp}
                    onCopyCode={(code) => {
                      void copyText(code);
                      show("Code copied");
                    }}
                  />
                ) : null}
              </>
            ) : null}
            {cipher.type === 2 ? (
              <CopyField
                label="Note"
                value={cipher.notes || ""}
                onCopy={() => {
                  void copyText(cipher.notes || "");
                  show("Copied");
                }}
              />
            ) : null}
            {cipher.type === 3 && cipher.card ? (
              <>
                <CopyField label="Name on card" value={cipher.card.cardholderName || ""} onCopy={() => { void copyText(cipher.card!.cardholderName || ""); show("Copied"); }} />
                <CopyField label="Number" value={cipher.card.number || ""} onCopy={() => { void copyText(cipher.card!.number || ""); show("Copied"); }} />
                <CopyField label="Expiry" value={`${cipher.card.expMonth || ""}/${cipher.card.expYear || ""}`} onCopy={() => { void copyText(`${cipher.card!.expMonth}/${cipher.card!.expYear}`); show("Copied"); }} />
                <CopyField label="CVV" value={cipher.card.code || ""} onCopy={() => { void copyText(cipher.card!.code || ""); show("Copied"); }} />
              </>
            ) : null}
            {cipher.type === 4 && cipher.identity ? (
              <>
                <CopyField label="Name" value={`${cipher.identity.firstName || ""} ${cipher.identity.lastName || ""}`.trim()} onCopy={() => { void copyText(`${cipher.identity!.firstName || ""} ${cipher.identity!.lastName || ""}`.trim()); show("Copied"); }} />
                <CopyField label="Email" value={cipher.identity.email || ""} onCopy={() => { void copyText(cipher.identity!.email || ""); show("Copied"); }} />
                <CopyField label="Phone" value={cipher.identity.phone || ""} onCopy={() => { void copyText(cipher.identity!.phone || ""); show("Copied"); }} />
              </>
            ) : null}
            {cipher.notes && cipher.type !== 2 ? (
              <CopyField
                label="Notes"
                value={cipher.notes}
                onCopy={() => {
                  void copyText(cipher.notes || "");
                  show("Copied");
                }}
              />
            ) : null}
            {cipher.id ? (
              <div style={{ marginTop: "1rem" }}>
                <p className="sp-section-title">Attachments</p>
                <input
                  type="file"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (!file) return;
                    try {
                      await vault.uploadAttachment(cipher.id, file);
                      const r = await vault.listAttachments(cipher.id);
                      setAttachments(r.data || []);
                      show("Attachment uploaded");
                    } catch (err) {
                      setError(err instanceof Error ? err.message : "Upload failed");
                    }
                  }}
                />
                <div className="sp-list" style={{ marginTop: "0.5rem" }}>
                  {attachments.map((a) => (
                    <div key={String(a.id)} className="sp-item" style={{ cursor: "default" }}>
                      <div className="sp-avatar">A</div>
                      <div className="sp-item-body">
                        <strong>{String(a.fileName)}</strong>
                        <span>{String(a.size)} bytes</span>
                      </div>
                      <button
                        type="button"
                        className="sp-icon-btn"
                        onClick={async () => {
                          try {
                            const { fileName, bytes } = await vault.downloadAttachment(
                              cipher.id,
                              String(a.id)
                            );
                            const blob = new Blob([new Uint8Array(bytes)]);
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement("a");
                            link.href = url;
                            link.download = fileName;
                            link.click();
                            URL.revokeObjectURL(url);
                          } catch (err) {
                            setError(err instanceof Error ? err.message : "Download failed");
                          }
                        }}
                      >
                        ↓
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "1.1rem" }}>
              <button type="button" className="sp-btn" onClick={() => setEditing(true)}>
                Edit
              </button>
              {cipher.id ? (
                <button
                  type="button"
                  className="sp-btn sp-btn-ghost"
                  onClick={async () => {
                    try {
                      await vault.toggleFavorite(cipher.id);
                      onSaved();
                      show(cipher.favorite ? "Unstarred" : "Starred");
                    } catch (err) {
                      setError(err instanceof Error ? err.message : "Failed");
                    }
                  }}
                >
                  {cipher.favorite ? "Unstar" : "Star"}
                </button>
              ) : null}
              {cipher.id ? (
                <button type="button" className="sp-btn sp-btn-danger" disabled={busy} onClick={remove}>
                  Delete
                </button>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <div className="sp-field">
              <label>Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            {cipher.type === 1 ? (
              <>
                <div className="sp-field">
                  <label>Username</label>
                  <input value={username} onChange={(e) => setUsername(e.target.value)} />
                </div>
                <div className="sp-field">
                  <label>Password</label>
                  <input value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                <div className="sp-field">
                  <label>Website</label>
                  <input value={uri} onChange={(e) => setUri(e.target.value)} />
                </div>
                <div className="sp-field">
                  <label>Authenticator (TOTP) key</label>
                  <input value={totp} onChange={(e) => setTotp(e.target.value)} />
                </div>
              </>
            ) : null}
            {cipher.type === 3 ? (
              <>
                <div className="sp-field">
                  <label>Cardholder</label>
                  <input value={cardholderName} onChange={(e) => setCardholderName(e.target.value)} />
                </div>
                <div className="sp-field">
                  <label>Brand</label>
                  <input value={brand} onChange={(e) => setBrand(e.target.value)} />
                </div>
                <div className="sp-field">
                  <label>Number</label>
                  <input value={number} onChange={(e) => setNumber(e.target.value)} />
                </div>
                <div className="sp-field">
                  <label>Exp month</label>
                  <input value={expMonth} onChange={(e) => setExpMonth(e.target.value)} />
                </div>
                <div className="sp-field">
                  <label>Exp year</label>
                  <input value={expYear} onChange={(e) => setExpYear(e.target.value)} />
                </div>
                <div className="sp-field">
                  <label>CVV</label>
                  <input value={code} onChange={(e) => setCode(e.target.value)} />
                </div>
              </>
            ) : null}
            {cipher.type === 4 ? (
              <>
                <div className="sp-field">
                  <label>First name</label>
                  <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </div>
                <div className="sp-field">
                  <label>Last name</label>
                  <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </div>
                <div className="sp-field">
                  <label>Email</label>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div className="sp-field">
                  <label>Phone</label>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
                <div className="sp-field">
                  <label>Address</label>
                  <input value={address1} onChange={(e) => setAddress1(e.target.value)} />
                </div>
                <div className="sp-field">
                  <label>City</label>
                  <input value={city} onChange={(e) => setCity(e.target.value)} />
                </div>
              </>
            ) : null}
            <div className="sp-field">
              <label>Folder</label>
              <select value={folderId} onChange={(e) => setFolderId(e.target.value)}>
                <option value="">None</option>
                {vault.folders.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="sp-field">
              <label>Notes</label>
              <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
            <label className="sp-check" style={{ marginBottom: "1rem" }}>
              <input
                type="checkbox"
                checked={favorite}
                onChange={(e) => setFavorite(e.target.checked)}
              />
              Favorite
            </label>
            <label className="sp-check" style={{ marginBottom: "1rem" }}>
              <input
                type="checkbox"
                checked={reprompt}
                onChange={(e) => setReprompt(e.target.checked)}
              />
              Require master password to view
            </label>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <button type="button" className="sp-btn" disabled={busy} onClick={save}>
                Save
              </button>
              <button
                type="button"
                className="sp-btn sp-btn-ghost"
                onClick={() => (cipher.id ? setEditing(false) : onClose())}
              >
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function LiveTotpField({
  secret,
  onCopyCode,
}: {
  secret: string;
  onCopyCode: (code: string) => void;
}) {
  const [code, setCode] = useState("------");
  const [remaining, setRemaining] = useState(30);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const t = await generateTotp(secret);
        if (cancelled) return;
        setCode(t.code);
        setRemaining(t.remaining);
        setPeriod(t.period);
      } catch {
        if (!cancelled) setCode("------");
      }
    };
    void tick();
    const id = setInterval(() => void tick(), 1000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [secret]);

  return (
    <div className="sp-copy-row">
      <div style={{ minWidth: 0, flex: 1 }}>
        <label>Authenticator code · {remaining}s</label>
        <div className="val" style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "0.12em" }}>
          {code}
        </div>
        <div
          className="sp-totp-bar"
          style={{
            marginTop: "0.35rem",
            height: 3,
            borderRadius: 2,
            background: "var(--sp-border, #2a3344)",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${Math.max(0, (remaining / period) * 100)}%`,
              background: remaining <= 5 ? "#e85d4c" : "var(--sp-accent, #3d8bfd)",
              transition: "width 1s linear",
            }}
          />
        </div>
      </div>
      <button
        type="button"
        className="sp-icon-btn"
        onClick={() => onCopyCode(code)}
        title="Copy code"
      >
        ⎘
      </button>
    </div>
  );
}

function CopyField({
  label,
  value,
  raw,
  onCopy,
  extra,
}: {
  label: string;
  value: string;
  raw?: string;
  onCopy: () => void;
  extra?: React.ReactNode;
}) {
  if (!value && !raw) return null;
  return (
    <div className="sp-copy-row">
      <div style={{ minWidth: 0, flex: 1 }}>
        <label>{label}</label>
        <div className="val">{value || "—"}</div>
      </div>
      <div style={{ display: "flex", gap: "0.25rem" }}>
        {extra}
        <button type="button" className="sp-icon-btn" onClick={onCopy} title="Copy">
          ⎘
        </button>
      </div>
    </div>
  );
}
